import AddIcon from '@offline-mui/icons-material/Add'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useMemo, useState } from 'react'
import { accountingRequest, accountingUrl, formatMoney, jsonInit, round2, when } from '../api'
import { LeoBrand, LeoHeader } from '../LeoHeader'
import { InvoiceSearchForm, InvoiceSearchResults, SearchOutcome } from './InvoiceSearch'

const LINE_HEADERS = ['Code', 'Date', 'Item', 'Total', 'Our COMM', 'Agency COMM', 'Profit']
const GST_RATE = 0.05
const STATUSES = ['open', 'closed']
// SND-14217 check types. 91 and 93 differ by bank; Finance will confirm the bank names.
const CHECK_TYPES = [
  { code: 98, label: 'Dummy Check' },
  { code: 91, label: 'Agency Commission Check' },
  { code: 93, label: 'Agency Commission Check' },
]

function checkTypeLabel(code: unknown) {
  const found = CHECK_TYPES.find((row) => row.code === Number(code))
  return found ? `${found.code} - ${found.label}` : ''
}

type Money = (value: unknown) => string

function Facts({ rows }: { rows: [string, any][] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', columnGap: 2, rowGap: 1 }}>
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ display: 'contents' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="body2" component="div" sx={{ fontWeight: 700 }}>{value || '—'}</Typography>
        </Box>
      ))}
    </Box>
  )
}

// Supplier code and name on one line, the line's transaction type underneath.
function LineCode({ line }: { line: any }) {
  return (
    <>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ fontWeight: 700 }}>{line.code || '—'}</Box>
        {line.supplierName ? <Box component="span" sx={{ ml: 1 }}>{line.supplierName}</Box> : null}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Type: <Box component="span" sx={{ fontWeight: 700, color: line.txnType ? 'info.main' : 'text.disabled' }}>{line.txnType || 'Not set'}</Box>
      </Typography>
    </>
  )
}

// The "Needs check" chip opens Add payment for the line; Refund opens a new line pre-filled from it (type RC).
function LineActions({
  line,
  onAddPayment,
  onRefund,
}: {
  line: any
  onAddPayment: (line: any) => void
  onRefund: (line: any) => void
}) {
  const nudge = line.paymentNudge
  // Refund (RC) lines, and lines already refunded, show neither the check nudge nor Refund.
  if (String(line.txnType || '').toUpperCase() === 'RC' || line.refunded) return null
  const canRefund = /^BSP/i.test(String(line.supplierName || '').trim())
  if (!nudge && !canRefund) return null
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {nudge ? (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          clickable
          onClick={() => onAddPayment(line)}
          label={`Needs check ${nudge.checkTypes.join(' or ')}`}
          title={`${nudge.message} Click to add the payment.`}
        />
      ) : null}
      {canRefund ? (
        <Link component="button" type="button" variant="caption" onClick={() => onRefund(line)} sx={{ fontWeight: 600 }}>
          Refund
        </Link>
      ) : null}
    </Stack>
  )
}

function LineItem({ line }: { line: any }) {
  const description = line.description || line.passengerName || ''
  const airline = line.airlineName || line.airlineCode || ''
  return (
    <>
      <Typography variant="body2">{description || '—'}</Typography>
      {line.ticketNumber ? <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>{line.ticketNumber}</Typography> : null}
      {airline ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Airline — {airline}
        </Typography>
      ) : null}
      {line.readOnly ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Closed</Typography> : null}
    </>
  )
}

function StatusSelect({ value, onChange, disabled, label }: { value: string; onChange: (status: string) => void; disabled?: boolean; label: string }) {
  return (
    <TextField
      select
      size="small"
      aria-label={label}
      value={value || 'open'}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      sx={{ minWidth: 110, '& .MuiSelect-select': { py: 0.5, textTransform: 'capitalize', fontWeight: 600, color: value === 'closed' ? 'error.main' : 'success.dark' } }}
    >
      {STATUSES.map((status) => (
        <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>{status}</MenuItem>
      ))}
    </TextField>
  )
}

const EMPTY_LINE_DRAFT = {
  supplierCode: '',
  txnType: '',
  airlineCode: '',
  description: '',
  ticketNumber: '',
  txnValue: '',
  txnOtherTaxes: '',
  txnGst: '',
  txnQst: '',
  txnHst: '',
  txnTotal: '',
  ourValue: '',
  ourGst: '',
  ourQst: '',
  ourHst: '',
  ourTotal: '',
  agencyValue: '',
  agencyGst: '',
  agencyQst: '',
  agencyHst: '',
  agencyTotal: '',
}

function moneyStr(value: unknown) {
  if (value == null || value === '') return ''
  const number = Number(value)
  return Number.isFinite(number) ? String(number) : ''
}

function draftFromLine(line: any) {
  const breakdown = line?.breakdown || {}
  const our = line?.ourCommissionBreakdown || {}
  const agency = line?.agencyCommissionBreakdown || {}
  return {
    ...EMPTY_LINE_DRAFT,
    supplierCode: line?.code || '',
    txnType: line?.txnType || '',
    airlineCode: line?.airlineCode || '',
    description: line?.description || line?.passengerName || '',
    ticketNumber: line?.ticketNumber || '',
    txnValue: moneyStr(breakdown.base),
    txnOtherTaxes: moneyStr(breakdown.otherTaxes),
    txnGst: moneyStr(breakdown.gst),
    txnQst: moneyStr(breakdown.qst),
    txnHst: moneyStr(breakdown.hst),
    ourValue: moneyStr(our.value ?? line?.ourCommission),
    ourGst: moneyStr(our.gst),
    ourQst: moneyStr(our.qst),
    ourHst: moneyStr(our.hst),
    agencyValue: moneyStr(agency.value ?? line?.agencyCommission),
    agencyGst: moneyStr(agency.gst),
    agencyQst: moneyStr(agency.qst),
    agencyHst: moneyStr(agency.hst),
  }
}

// Refund pre-fill: the original amounts with the sign flipped (blank and zero stay as they are).
const REFUND_NEGATED_FIELDS = [
  'txnValue', 'txnOtherTaxes', 'txnGst', 'txnQst', 'txnHst',
  'ourValue', 'ourGst', 'ourQst', 'ourHst',
  'agencyValue', 'agencyGst', 'agencyQst', 'agencyHst',
] as const

function refundDraftFromLine(line: any) {
  const draft: any = { ...draftFromLine(line), txnType: 'RC' }
  for (const key of REFUND_NEGATED_FIELDS) {
    const number = Number(draft[key])
    if (draft[key] !== '' && Number.isFinite(number) && number !== 0) draft[key] = String(round2(-number))
  }
  return draft as typeof EMPTY_LINE_DRAFT
}

function moneyField(value: string) {
  if (value === '' || value == null) return undefined
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function sumMoney(...values: string[]) {
  return round2(values.reduce((sum, value) => sum + (Number(value) || 0), 0))
}

function MoneyField({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string
  value: string
  onChange?: (value: string) => void
  readOnly?: boolean
}) {
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={value}
      onChange={onChange && !readOnly ? (event) => onChange(event.target.value) : undefined}
      slotProps={{ htmlInput: { step: '0.01' }, input: readOnly ? { readOnly: true } : undefined }}
    />
  )
}

function LineFormDialog({
  open,
  line,
  template,
  invoiceNumber,
  recordLocator,
  onClose,
  onSaved,
}: {
  open: boolean
  line?: any
  // Refund: a new line pre-filled from this one, with transaction type RC. Saving adds a line.
  template?: any
  invoiceNumber: string
  recordLocator: string
  onClose: () => void
  onSaved: (invoice: any) => void
}) {
  const editing = Boolean(line)
  const closed = Boolean(line?.readOnly || String(line?.status || '').toLowerCase() === 'closed')
  const [draft, setDraft] = useState({ ...EMPTY_LINE_DRAFT })
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [txnTypes, setTxnTypes] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setDraft(
      line
        ? draftFromLine(line)
        : template
          ? refundDraftFromLine(template)
          : { ...EMPTY_LINE_DRAFT },
    )
    setError('')
    accountingRequest('/reference/transaction-types')
      .then((rows) => setTxnTypes(Array.isArray(rows) ? rows.filter((row) => row.active !== false) : []))
      .catch(() => setTxnTypes([]))
    accountingRequest('/reference/suppliers')
      .then((rows) => setSuppliers(Array.isArray(rows) ? rows.filter((row) => row.active !== false) : []))
      .catch(() => setSuppliers([]))
  }, [open, line, template])

  const txnTotal = useMemo(
    () => (draft.txnTotal !== '' ? Number(draft.txnTotal) || 0 : sumMoney(draft.txnValue, draft.txnOtherTaxes, draft.txnGst, draft.txnQst, draft.txnHst)),
    [draft.txnTotal, draft.txnValue, draft.txnOtherTaxes, draft.txnGst, draft.txnQst, draft.txnHst],
  )
  const ourTotal = useMemo(
    () => (draft.ourTotal !== '' ? Number(draft.ourTotal) || 0 : sumMoney(draft.ourValue, draft.ourGst, draft.ourQst, draft.ourHst)),
    [draft.ourTotal, draft.ourValue, draft.ourGst, draft.ourQst, draft.ourHst],
  )
  const agencyTotal = useMemo(
    () => (draft.agencyTotal !== '' ? Number(draft.agencyTotal) || 0 : sumMoney(draft.agencyValue, draft.agencyGst, draft.agencyQst, draft.agencyHst)),
    [draft.agencyTotal, draft.agencyValue, draft.agencyGst, draft.agencyQst, draft.agencyHst],
  )

  function setField(key: keyof typeof EMPTY_LINE_DRAFT, value: string) {
    if (closed) return
    setDraft((current) => ({ ...current, [key]: value }))
  }

  function onOurValueChange(value: string) {
    if (closed) return
    const gst = value === '' ? '' : String(round2((Number(value) || 0) * GST_RATE))
    setDraft((current) => ({ ...current, ourValue: value, ourGst: gst, ourTotal: '' }))
  }

  function onAgencyValueChange(value: string) {
    if (closed) return
    const gst = value === '' ? '' : String(round2((Number(value) || 0) * GST_RATE))
    setDraft((current) => ({ ...current, agencyValue: value, agencyGst: gst, agencyTotal: '' }))
  }

  async function save(event: any) {
    event.preventDefault()
    if (closed) return
    if (!draft.supplierCode) {
      setError('Supplier code is required.')
      return
    }
    const supplier = suppliers.find((row) => String(row.code).toUpperCase() === draft.supplierCode.toUpperCase())
    const ourValue = moneyField(draft.ourValue) ?? 0
    const agencyValue = moneyField(draft.agencyValue) ?? 0
    const payload = {
      // A refund records the line it refunds, so that line stops offering Refund and the check nudge.
      refundOf: !editing && template?.lineIdentifier ? template.lineIdentifier : undefined,
      supplierCode: draft.supplierCode,
      txnType: draft.txnType || null,
      supplierName: supplier?.name_en || supplier?.name || line?.supplierName || draft.supplierCode,
      airlineCode: draft.airlineCode.trim() || undefined,
      description: draft.description.trim() || undefined,
      ticketNumber: draft.ticketNumber.trim() || undefined,
      status: line?.status || 'Open',
      date: editing ? undefined : new Date().toISOString(),
      profit: round2(ourValue - agencyValue),
      breakdown: {
        value: moneyField(draft.txnValue) ?? 0,
        otherTaxes: moneyField(draft.txnOtherTaxes) ?? 0,
        gst: moneyField(draft.txnGst) ?? 0,
        qst: moneyField(draft.txnQst) ?? 0,
        hst: moneyField(draft.txnHst) ?? 0,
        total: txnTotal,
      },
      ourCommissionBreakdown: {
        value: ourValue,
        gst: moneyField(draft.ourGst) ?? 0,
        qst: moneyField(draft.ourQst) ?? 0,
        hst: moneyField(draft.ourHst) ?? 0,
        total: ourTotal,
      },
      agencyCommissionBreakdown: {
        value: agencyValue,
        gst: moneyField(draft.agencyGst) ?? 0,
        qst: moneyField(draft.agencyQst) ?? 0,
        hst: moneyField(draft.agencyHst) ?? 0,
        total: agencyTotal,
      },
    }
    setSaving(true)
    setError('')
    try {
      const path = editing
        ? `/invoices/${encodeURIComponent(recordLocator)}/lines/${encodeURIComponent(line.lineIdentifier)}`
        : `/invoices/${encodeURIComponent(recordLocator)}/lines`
      const invoice = await accountingRequest(path, jsonInit(editing ? 'PUT' : 'POST', payload))
      onSaved(invoice)
      onClose()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box component="form" onSubmit={save}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? (line.lineIdentifier || 'Line item') : template ? 'Refund line item' : 'Add line item'}
          {closed ? <Chip size="small" label="Closed · read only" sx={{ ml: 1.5 }} /> : null}
          {!editing ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
              Invoice {invoiceNumber}
              {template?.lineIdentifier ? ` · refund of ${template.lineIdentifier}` : ''}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

          <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Top section</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              select
              required
              size="small"
              label="Supplier code"
              value={draft.supplierCode}
              disabled={closed}
              onChange={(event) => setField('supplierCode', event.target.value)}
            >
              <MenuItem value="">Select supplier</MenuItem>
              {suppliers.map((row) => (
                <MenuItem key={row.code} value={row.code}>
                  {row.code} — {row.name_en || row.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Airline code"
              value={draft.airlineCode}
              disabled={closed}
              onChange={(event) => setField('airlineCode', event.target.value)}
              helperText="Numeric IATA (e.g. 014) stores carrier name"
            />
            <TextField
              size="small"
              label="Description"
              value={draft.description}
              disabled={closed}
              onChange={(event) => setField('description', event.target.value)}
              helperText="Passenger name and other free-text details"
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              size="small"
              label="Ticket number"
              value={draft.ticketNumber}
              disabled={closed}
              onChange={(event) => setField('ticketNumber', event.target.value)}
            />
            <TextField
              select
              size="small"
              label="Transaction type"
              value={draft.txnType}
              disabled={closed}
              onChange={(event) => setField('txnType', event.target.value)}
            >
              <MenuItem value="">Not set</MenuItem>
              {draft.txnType && !txnTypes.some((row) => row.code === draft.txnType) ? (
                <MenuItem value={draft.txnType}>{draft.txnType}</MenuItem>
              ) : null}
              {txnTypes.map((row) => (
                <MenuItem key={row.code} value={row.code}>
                  {row.description ? `${row.code} - ${row.description}` : row.code}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Typography variant="overline" color="info.main" sx={{ display: 'block', mt: 3, fontWeight: 700 }}>Transaction breakdown</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 1 }}>
            <MoneyField label="Value" value={draft.txnValue} onChange={(value) => setField('txnValue', value)} readOnly={closed} />
            <MoneyField label="Other taxes" value={draft.txnOtherTaxes} onChange={(value) => setField('txnOtherTaxes', value)} readOnly={closed} />
            <MoneyField label="GST" value={draft.txnGst} onChange={(value) => setField('txnGst', value)} readOnly={closed} />
            <MoneyField label="QST" value={draft.txnQst} onChange={(value) => setField('txnQst', value)} readOnly={closed} />
            <MoneyField label="HST" value={draft.txnHst} onChange={(value) => setField('txnHst', value)} readOnly={closed} />
            <MoneyField label="Total including taxes" value={txnTotal.toFixed(2)} readOnly />
          </Box>

          <Typography variant="overline" color="info.main" sx={{ display: 'block', mt: 3, fontWeight: 700 }}>Our commission</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 1 }}>
            <MoneyField label="Value" value={draft.ourValue} onChange={onOurValueChange} readOnly={closed} />
            <MoneyField label="GST" value={draft.ourGst} onChange={(value) => setField('ourGst', value)} readOnly={closed} />
            <MoneyField label="QST" value={draft.ourQst} onChange={(value) => setField('ourQst', value)} readOnly={closed} />
            <MoneyField label="HST" value={draft.ourHst} onChange={(value) => setField('ourHst', value)} readOnly={closed} />
            <MoneyField label="Total including taxes" value={ourTotal.toFixed(2)} readOnly />
          </Box>

          <Typography variant="overline" color="info.main" sx={{ display: 'block', mt: 3, fontWeight: 700 }}>Agency commission</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 1 }}>
            <MoneyField label="Value" value={draft.agencyValue} onChange={onAgencyValueChange} readOnly={closed} />
            <MoneyField label="GST" value={draft.agencyGst} onChange={(value) => setField('agencyGst', value)} readOnly={closed} />
            <MoneyField label="QST" value={draft.agencyQst} onChange={(value) => setField('agencyQst', value)} readOnly={closed} />
            <MoneyField label="HST" value={draft.agencyHst} onChange={(value) => setField('agencyHst', value)} readOnly={closed} />
            <MoneyField label="Total including taxes" value={agencyTotal.toFixed(2)} readOnly />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>{closed ? 'Close' : 'Cancel'}</Button>
          {!closed ? (
            <Button type="submit" variant="contained" color="warning" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save line item' : template ? 'Add refund line' : 'Add line item'}
            </Button>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  )
}

const EMPTY_PAYMENT_DRAFT = {
  fopCode: '',
  checkType: '',
  lineIdentifier: '',
  debit: '',
  credit: '',
  reference: '',
  cardFirst6: '',
  cardOrCheck: '',
}

function amountStr(value: unknown) {
  const number = Number(value)
  return Number.isFinite(number) && number !== 0 ? String(number) : ''
}

function draftFromPayment(payment: any) {
  return {
    ...EMPTY_PAYMENT_DRAFT,
    fopCode: payment?.fopCode || '',
    checkType: payment?.checkType != null ? String(payment.checkType) : '',
    lineIdentifier: payment?.lineIdentifier || '',
    debit: amountStr(payment?.debit),
    credit: amountStr(payment?.credit),
    reference: payment?.reference || '',
    cardFirst6: payment?.cardFirst6 || '',
    cardOrCheck: payment?.cardOrCheck || '',
  }
}

function isCardFop(formsOfPayment: any[], code: string) {
  const row = formsOfPayment.find((item) => item.code === code)
  return /^credit\s*cards?$/i.test(String(row?.category || '').trim())
}

function PaymentFormDialog({
  open,
  recordLocator,
  invoiceNumber,
  lines,
  forLine,
  payment,
  onClose,
  onSaved,
}: {
  open: boolean
  recordLocator: string
  invoiceNumber: string
  lines: any[]
  forLine?: any
  payment?: any
  onClose: () => void
  onSaved: (invoice: any) => void
}) {
  const editing = Boolean(payment?.id)
  const [draft, setDraft] = useState({ ...EMPTY_PAYMENT_DRAFT })
  const [formsOfPayment, setFormsOfPayment] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const nudge = forLine?.paymentNudge
  const cardFop = isCardFop(formsOfPayment, draft.fopCode)

  useEffect(() => {
    if (!open) return
    const suggested = nudge?.checkTypes?.length === 1 ? String(nudge.checkTypes[0]) : ''
    setDraft(
      payment?.id
        ? draftFromPayment(payment)
        : { ...EMPTY_PAYMENT_DRAFT, lineIdentifier: forLine?.lineIdentifier || '', checkType: suggested },
    )
    setError('')
    accountingRequest('/reference/forms-of-payment')
      .then((rows) => setFormsOfPayment(Array.isArray(rows) ? rows.filter((row) => row.active !== false) : []))
      .catch(() => setFormsOfPayment([]))
  }, [open, forLine, nudge, payment])

  function setField(key: keyof typeof EMPTY_PAYMENT_DRAFT, value: string) {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  async function save(event: any) {
    event.preventDefault()
    if (!draft.fopCode) {
      setError('Form of payment code is required.')
      return
    }
    const debit = Number(draft.debit) || 0
    const credit = Number(draft.credit) || 0
    if (!debit && !credit) {
      setError('Enter a debit or a credit amount.')
      return
    }
    if (cardFop && draft.cardFirst6 && !/^\d{6}$/.test(draft.cardFirst6)) {
      setError('Card first 6 must be exactly 6 digits.')
      return
    }
    if (draft.cardOrCheck) {
      if (cardFop && !/^\d{4}$/.test(draft.cardOrCheck)) {
        setError('Card number must be the last 4 digits only.')
        return
      }
      if (!cardFop && !/^[A-Za-z0-9-]{1,20}$/.test(draft.cardOrCheck)) {
        setError('Check number can have up to 20 letters, digits or dashes.')
        return
      }
    }
    setSaving(true)
    setError('')
    try {
      const path = editing
        ? `/invoices/${encodeURIComponent(recordLocator)}/payments/${encodeURIComponent(payment.id)}`
        : `/invoices/${encodeURIComponent(recordLocator)}/payments`
      const invoice = await accountingRequest(
        path,
        jsonInit(editing ? 'PUT' : 'POST', {
          fopCode: draft.fopCode,
          checkType: draft.checkType ? Number(draft.checkType) : null,
          lineIdentifier: draft.lineIdentifier || undefined,
          debit,
          credit,
          reference: draft.reference.trim() || undefined,
          cardOrCheck: draft.cardOrCheck || undefined,
          cardFirst6: cardFop ? draft.cardFirst6 || undefined : undefined,
        }),
      )
      onSaved(invoice)
      onClose()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={save}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Payment' : 'Add payment'}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            Invoice {invoiceNumber}{editing && payment.paidAt ? ` · paid ${when(payment.paidAt)}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {nudge ? <Alert severity="warning" sx={{ mb: 2 }}>{nudge.message}</Alert> : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              select
              required
              size="small"
              label="FoP code"
              value={draft.fopCode}
              onChange={(event) => setField('fopCode', event.target.value)}
            >
              <MenuItem value="">Select form of payment</MenuItem>
              {draft.fopCode && !formsOfPayment.some((row) => row.code === draft.fopCode) ? (
                <MenuItem value={draft.fopCode}>{draft.fopCode}</MenuItem>
              ) : null}
              {formsOfPayment.map((row) => (
                <MenuItem key={row.code} value={row.code}>
                  {row.code} - {row.description_en || row.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Check type"
              value={draft.checkType}
              onChange={(event) => setField('checkType', event.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {CHECK_TYPES.map((row) => (
                <MenuItem key={row.code} value={String(row.code)}>{checkTypeLabel(row.code)}</MenuItem>
              ))}
            </TextField>
            <MoneyField label="Debit" value={draft.debit} onChange={(value) => setField('debit', value)} />
            <MoneyField label="Credit" value={draft.credit} onChange={(value) => setField('credit', value)} />
            <TextField
              size="small"
              label="Reference"
              value={draft.reference}
              onChange={(event) => setField('reference', event.target.value)}
            />
            {cardFop ? (
              <TextField
                size="small"
                label="Card first 6"
                value={draft.cardFirst6}
                onChange={(event) => setField('cardFirst6', event.target.value.replace(/\D/g, '').slice(0, 6))}
                helperText="First 6 digits, used by invoice search"
                slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6, autoComplete: 'off' } }}
              />
            ) : null}
            <TextField
              size="small"
              label="Card/Check Number"
              value={draft.cardOrCheck}
              onChange={(event) =>
                setField(
                  'cardOrCheck',
                  cardFop
                    ? event.target.value.replace(/\D/g, '').slice(0, 4)
                    : event.target.value.replace(/[^A-Za-z0-9-]/g, '').slice(0, 20),
                )
              }
              helperText={cardFop ? 'Last 4 digits of the card' : 'Check number'}
              slotProps={{ htmlInput: { inputMode: cardFop ? 'numeric' : 'text', maxLength: cardFop ? 4 : 20, autoComplete: 'off' } }}
            />
            <TextField
              select
              size="small"
              label="For line item"
              value={draft.lineIdentifier}
              onChange={(event) => setField('lineIdentifier', event.target.value)}
              helperText="Linking a line clears its payment nudge"
              sx={{ gridColumn: '1 / -1' }}
            >
              <MenuItem value="">Not linked to a line</MenuItem>
              {lines.filter((line) => line.lineIdentifier).map((line) => (
                <MenuItem key={line.lineIdentifier} value={line.lineIdentifier}>
                  {line.lineIdentifier} — {line.description || line.supplierName || line.code}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" color="secondary" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save payment' : 'Add payment'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

function LinesTable({
  lines,
  money,
  invoiceNumber,
  recordLocator,
  onInvoiceChange,
  onAddPayment,
}: {
  lines: any[]
  money: Money
  invoiceNumber: string
  recordLocator: string
  onInvoiceChange: (invoice: any) => void
  onAddPayment: (line: any) => void
}) {
  const [selected, setSelected] = useState<any>(null)
  const [adding, setAdding] = useState(false)
  const [refundOf, setRefundOf] = useState<any>(null)
  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Line items</Typography>
        <Button
          variant="contained"
          color="warning"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelected(null)
            setRefundOf(null)
            setAdding(true)
          }}
        >
          Add line item
        </Button>
      </Stack>
      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {LINE_HEADERS.map((label, index) => (
                <TableCell key={label} align={index < 3 ? 'left' : 'right'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <TableRow key={line.lineIdentifier || `${line.ticketNumber || line.code}-${index}`} sx={{ bgcolor: index % 2 ? 'rgba(29, 78, 137, 0.06)' : 'background.paper' }}>
                <TableCell><LineCode line={line} /></TableCell>
                <TableCell>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setAdding(false)
                      setRefundOf(null)
                      setSelected(line)
                    }}
                    sx={{ fontWeight: 600 }}
                  >
                    {when(line.date) || '—'}
                  </Link>
                </TableCell>
                <TableCell>
                  <LineItem line={line} />
                  <LineActions
                    line={line}
                    onAddPayment={onAddPayment}
                    onRefund={(source) => {
                      setAdding(false)
                      setSelected(null)
                      setRefundOf(source)
                    }}
                  />
                </TableCell>
                <TableCell align="right">{line.kind === 'loyalty' ? '' : money(line.total)}</TableCell>
                <TableCell align="right">{money(line.ourCommission)}</TableCell>
                <TableCell align="right">{money(line.agencyCommission)}</TableCell>
                <TableCell align="right" sx={{ color: Number(line.profit) < 0 ? 'error.main' : 'success.dark', fontWeight: 700 }}>
                  {money(line.profit)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <LineFormDialog
          open={adding || Boolean(selected) || Boolean(refundOf)}
          line={adding || refundOf ? undefined : selected}
          template={refundOf || undefined}
          invoiceNumber={invoiceNumber}
          recordLocator={recordLocator}
          onClose={() => {
            setAdding(false)
            setSelected(null)
            setRefundOf(null)
          }}
          onSaved={onInvoiceChange}
        />
      </TableContainer>
    </Box>
  )
}

export default function InvoicePage() {
  const [locator, setLocator] = useState('')
  const [draft, setDraft] = useState('')
  const [invoice, setInvoice] = useState<any>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState('')
  const [paymentDialog, setPaymentDialog] = useState<{ open: boolean; line?: any; payment?: any }>({ open: false })
  const [search, setSearch] = useState<SearchOutcome | null>(null)
  const [showResults, setShowResults] = useState(false)

  async function loadInvoice(pnr: string, path: string, init?: RequestInit) {
    setLoading(true)
    setError('')
    setInvoice(null)
    setLocator(pnr)
    try {
      setInvoice(await accountingRequest(path, init))
    } catch (err: any) {
      setInvoice(null)
      setError(err.message)
    } finally {
      setLoading(false)
      setBusy('')
    }
  }

  function openFromSearch(pnr: string) {
    setDraft(pnr)
    setBusy('open')
    setShowResults(false)
    loadInvoice(pnr, `/invoices/${encodeURIComponent(pnr)}`)
  }

  // One match opens the invoice; otherwise the results replace the open invoice until one is picked.
  function onSearchResults(outcome: SearchOutcome) {
    setSearch(outcome)
    if (outcome.results.length === 1) {
      openFromSearch(outcome.results[0].recordLocator)
      return
    }
    setError('')
    setShowResults(true)
  }

  function clearSearch() {
    setSearch(null)
    setShowResults(false)
  }

  function onImportSubmit(event: any) {
    event.preventDefault()
    importPnr()
  }

  function importPnr() {
    const pnr = draft.trim().toUpperCase()
    if (!pnr) return
    setBusy('import')
    setShowResults(false)
    loadInvoice(pnr, '/invoices/import', jsonInit('POST', { recordLocator: pnr }))
  }

  async function patchStatus(path: string, status: string) {
    setBusy('status')
    setError('')
    try {
      setInvoice(await accountingRequest(path, jsonInit('PATCH', { status })))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  async function downloadPdf() {
    const response = await fetch(accountingUrl(`/invoices/${invoice.recordLocator}/pdf`))
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      setError(body.message || 'Could not download the invoice.')
      return
    }
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `invoice-${invoice.recordLocator}.pdf`
    link.click()
    URL.revokeObjectURL(url)
  }

  const money: Money = (value) => formatMoney(value, invoice?.flightType)
  const lines: any[] = invoice?.supplierLines || []
  const agency = invoice?.agency || {}
  const office = invoice?.office || {}

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <LeoHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <LeoBrand />
          <Stack component="form" direction="row" spacing={1} onSubmit={onImportSubmit} sx={{ ml: 'auto', alignItems: 'center' }}>
            <TextField
              size="small"
              value={draft}
              placeholder="PNR"
              slotProps={{ htmlInput: { maxLength: 6, 'aria-label': 'PNR to import' } }}
              onChange={(event) => setDraft(event.target.value.toUpperCase())}
              sx={{ width: 150, bgcolor: 'common.white', borderRadius: 1, input: { textTransform: 'uppercase' } }}
            />
            <Button type="submit" variant="contained" disabled={loading} sx={{ bgcolor: 'common.white', color: 'primary.main', '&:hover': { bgcolor: '#e7f4f3' } }}>
              Import PNR
            </Button>
            <Button
              type="button"
              variant="contained"
              color="secondary"
              disabled={!invoice || loading || showResults}
              onClick={downloadPdf}
              sx={{ '&.Mui-disabled': { opacity: 1, bgcolor: '#efeafb', color: '#3d2f6b' } }}
            >
              Create Invoice
            </Button>
          </Stack>
        </Box>
        <Divider sx={{ my: 2.5, borderColor: 'rgba(255,255,255,0.35)' }} />
        <InvoiceSearchForm onResults={onSearchResults} onClear={clearSearch} />
      </LeoHeader>

      {showResults && search ? <InvoiceSearchResults outcome={search} onOpenInvoice={openFromSearch} /> : null}
      {!showResults && invoice && search && search.results.length > 1 ? (
        <Button size="small" onClick={() => setShowResults(true)} sx={{ mt: 2 }}>
          ← Back to search results ({search.results.length})
        </Button>
      ) : null}

      {loading ? (
        <Typography sx={{ mt: 2 }} color="info.main">{busy === 'import' ? 'Importing' : 'Loading'} {locator}…</Typography>
      ) : null}
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

      {invoice && !showResults ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr', gap: 2, mt: 3 }}>
            <Card sx={{ borderTop: '4px solid', borderTopColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>Agency</Typography>
                <Facts
                  rows={[
                    ['Name', agency.name],
                    ['Code', agency.code || invoice.agencyCode],
                    ['Phone', agency.phone],
                    ['Address', agency.address],
                    ['Network ID', agency.networkId],
                    ['Email', agency.email],
                  ]}
                />
                <Typography variant="overline" color="info.main" sx={{ display: 'block', mt: 2, fontWeight: 700 }}>Booking</Typography>
                <Facts
                  rows={[
                    ['Invoice number', invoice.invoiceNumber],
                    ['Status', <StatusSelect label="Invoice status" value={invoice.status} disabled={busy === 'status'} onChange={(status) => patchStatus(`/invoices/${invoice.recordLocator}/status`, status)} />],
                    ['PNR', invoice.recordLocator],
                    ['User', invoice.userName],
                    ['Provider', `${invoice.providerName || ''} · ${invoice.pcc || ''}`.replace(/^ · | · $/g, '')],
                    ['Flight type', invoice.flightType],
                    ['Departs', when(invoice.trip?.departs)],
                    ['Booked', when(invoice.bookingDate)],
                    ['Ticketed', when(invoice.ticketedAt)],
                    ['Promo', invoice.promoCode],
                  ]}
                />
              </CardContent>
            </Card>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <Card sx={{ borderTop: '4px solid', borderTopColor: 'secondary.main' }}>
                <CardContent>
                  <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 700 }}>Office</Typography>
                  <Facts
                    rows={[
                      ['Office', [office.code, office.name].filter(Boolean).join(' · ')],
                      ['Ticketing agent', office.ticketingAgent],
                      ['Invoicing agent', office.invoicingAgent],
                      ['Reservation agent', office.reservationAgent],
                    ]}
                  />
                </CardContent>
              </Card>
              <Card sx={{ borderTop: '4px solid', borderTopColor: 'warning.main', bgcolor: '#fff8ec', flex: 1, display: 'flex' }}>
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="overline" color="warning.dark">Summary</Typography>
                  <Stack spacing={1} sx={{ flex: 1, justifyContent: 'space-evenly' }}>
                    {([
                      ['Sales', money(invoice.summary.sales), false],
                      ['Our commission', money(invoice.summary.ourCommission), false],
                      ['With 5% GST', money(invoice.summary.ourCommissionWithGst), false],
                      ['Agency commission', money(invoice.summary.agencyCommission), false],
                      ['Intair profit', money(invoice.summary.intairProfit), false],
                      ['Loyalty', money(-invoice.summary.loyaltyDollars || 0), true],
                      ['Profit after loyalty', money(invoice.summary.profitAfterLoyalty), false],
                    ] as [string, string, boolean][]).map(([label, value, negative]) => (
                      <Stack key={label} direction="row" sx={{ justifyContent: 'space-between', borderBottom: 1, borderColor: 'warning.light', pb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: negative ? 'error.main' : 'text.primary' }}>{value}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Box>

          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1.5 }}>
            <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 700 }}>Payments</Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setPaymentDialog({ open: true })}
            >
              Add payment
            </Button>
          </Stack>
          <PaymentFormDialog
            open={paymentDialog.open}
            forLine={paymentDialog.line}
            payment={paymentDialog.payment}
            recordLocator={invoice.recordLocator}
            invoiceNumber={invoice.invoiceNumber}
            lines={lines}
            onClose={() => setPaymentDialog({ open: false })}
            onSaved={setInvoice}
          />
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'secondary.main' }}>
                  {['Date', 'FOP', 'Check type', 'Card/Check Number', 'Reference', 'Debit', 'Credit'].map((label, index) => (
                    <TableCell key={label} align={index >= 5 ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(invoice.payments || []).map((payment: any, index: number) => (
                  <TableRow key={payment.id || `${index}-${payment.fopCode}-${payment.reference}`} sx={{ bgcolor: index % 2 ? 'rgba(231, 111, 81, 0.08)' : 'background.paper' }}>
                    <TableCell>
                      {payment.id ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => setPaymentDialog({ open: true, payment })}
                          sx={{ fontWeight: 600 }}
                        >
                          {when(payment.paidAt) || '—'}
                        </Link>
                      ) : (
                        when(payment.paidAt)
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{payment.fopCode}</TableCell>
                    <TableCell>{checkTypeLabel(payment.checkType)}</TableCell>
                    <TableCell>{payment.cardOrCheckDisplay}</TableCell>
                    <TableCell>{payment.reference}</TableCell>
                    <TableCell align="right">{Number(payment.debit) ? money(payment.debit) : ''}</TableCell>
                    <TableCell align="right">{Number(payment.credit) ? money(payment.credit) : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <LinesTable
            lines={lines}
            money={money}
            invoiceNumber={invoice.invoiceNumber}
            recordLocator={invoice.recordLocator}
            onInvoiceChange={setInvoice}
            onAddPayment={(line) => setPaymentDialog({ open: true, line })}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Amounts are the booking values. Our commission with GST is 5% on top of the stored commission.
            100 loyalty points equal 1 dollar. Markup stays on the Intair card line and is left out of sales.
          </Typography>
        </>
      ) : null}
    </Box>
  )
}
